import { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Phone01Icon,
  Settings01Icon,
  ArrowLeftIcon,
  Send01Icon,
  Edit02Icon,
  XCloseIcon,
  CheckIcon,
  RefreshCcw01Icon,
} from '@untitledui-icons/react/line'
import { Button } from '@/components/base/buttons'
import { LoadingSpinner } from '@/components/common'
import { useIdentity, useConversation } from '@/hooks/api'
import {
  useTryAssistant,
  useStartConversation,
  useAssistantPhoneNumber,
  type ChatMessage,
} from '@/hooks/api/useTryAssistant'
import { cn } from '@/lib/utils'
import { truncateAfterEdit } from './tryAssistantMessages'

/**
 * Settings popover component for configuring caller number
 */
function SettingsPopover({
  isOpen,
  onClose,
  callerNumber,
  onCallerNumberChange,
  anchorRef,
}: {
  isOpen: boolean
  onClose: () => void
  callerNumber: string
  onCallerNumberChange: (number: string) => void
  anchorRef: React.RefObject<HTMLButtonElement | null>
}) {
  const { t } = useTranslation()
  const [tempNumber, setTempNumber] = useState(callerNumber)
  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setTempNumber(callerNumber)
  }, [callerNumber])

  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target as Node)
      ) {
        handleCancel()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, anchorRef, callerNumber])

  const handleApply = () => {
    if (tempNumber !== callerNumber) {
      onCallerNumberChange(tempNumber)
    }
    onClose()
  }

  const handleCancel = () => {
    setTempNumber(callerNumber)
    onClose()
  }

  const hasChanges = tempNumber !== callerNumber

  if (!isOpen) return null

  return (
    <div
      ref={popoverRef}
      className="absolute right-0 top-full mt-2 z-50 w-72 rounded-xl border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-800"
    >
      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">
        {t('pages.try_assistant.chat_settings')}
      </h3>
      <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
        <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1.5">
          {t('pages.try_assistant.caller_number')}
        </label>
        <div className="relative">
          <Phone01Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="tel"
            value={tempNumber}
            onChange={(e) => setTempNumber(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && hasChanges) {
                e.preventDefault()
                handleApply()
              }
            }}
            placeholder={t('pages.try_assistant.caller_number_placeholder')}
            className={cn(
              'w-full rounded-lg border border-gray-300 bg-white pl-10 pr-3 py-2 text-sm',
              'placeholder:text-gray-400',
              'focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-none',
              'dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100'
            )}
          />
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
          {t('pages.try_assistant.caller_number_help')}
        </p>
        <div className="flex items-center justify-end gap-2 mt-4">
          <Button color="secondary" size="sm" onPress={handleCancel}>
            {t('pages.try_assistant.cancel')}
          </Button>
          <Button
            color="primary"
            size="sm"
            onPress={handleApply}
            isDisabled={!hasChanges}
            iconLeading={CheckIcon}
          >
            {t('pages.try_assistant.apply')}
          </Button>
        </div>
      </div>
    </div>
  )
}

/**
 * Single chat message component with editing capability
 */
export function ChatMessageItem({
  message,
  index,
  isEditing,
  editContent,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onSaveAndResend,
  onEditContentChange,
}: {
  message: ChatMessage
  index: number
  isEditing: boolean
  editContent: string
  onStartEdit: (index: number) => void
  onCancelEdit: () => void
  onSaveEdit: (index: number) => void
  onSaveAndResend: (index: number) => void
  onEditContentChange: (content: string) => void
}) {
  const isEditable = message.content && !message.tool_calls
  // An edited user or tool message can be resent: the backend answers a tool message as the tool's result.
  const canResend = message.role === 'user' || message.role === 'tool'

  const getRoleStyles = (role: string) => {
    switch (role) {
      case 'user':
        return 'bg-brand-50 dark:bg-brand-900/20 border-l-4 border-l-brand-500'
      case 'assistant':
        return 'bg-gray-50 dark:bg-gray-800/50 border-l-4 border-l-gray-400'
      case 'system':
        return 'bg-amber-50 dark:bg-amber-900/20 border-l-4 border-l-amber-500'
      case 'tool':
        return 'bg-purple-50 dark:bg-purple-900/20 border-l-4 border-l-purple-500'
      default:
        return 'bg-gray-50 dark:bg-gray-800/50'
    }
  }

  const { t } = useTranslation()

  const getRoleLabel = (role: string) => {
    const key = `pages.try_assistant.roles.${role}`
    const translated = t(key)
    return translated !== key ? translated : role
  }

  const getMessageContent = () => {
    if (message.tool_calls) {
      const call = message.tool_calls[0]
      return `${call.function.name} => ${call.function.arguments}`
    }
    return message.content || ''
  }

  return (
    <div className={cn('p-4 transition-colors', getRoleStyles(message.role))}>
      {!isEditing ? (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {getRoleLabel(message.role)}
            </span>
            {isEditable && (
              <Button
                color="tertiary"
                size="sm"
                onPress={() => onStartEdit(index)}
                className="p-1 h-auto opacity-60 hover:opacity-100"
                iconLeading={Edit02Icon}
              />
            )}
          </div>
          <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
            {getMessageContent()}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {getRoleLabel(message.role)}
          </span>
          <textarea
            value={editContent}
            onChange={(e) => onEditContentChange(e.target.value)}
            rows={message.role === 'system' || message.role === 'tool' ? 10 : 4}
            autoFocus
            className={cn(
              'w-full rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-sm',
              'placeholder:text-gray-400 resize-y',
              'focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-none',
              'dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100'
            )}
          />
          <div className="flex items-center justify-end gap-2">
            <Button color="secondary" size="sm" onPress={onCancelEdit} iconLeading={XCloseIcon}>
              {t('pages.try_assistant.cancel')}
            </Button>
            <Button
              color={canResend ? 'secondary' : 'primary'}
              size="sm"
              onPress={() => onSaveEdit(index)}
              iconLeading={CheckIcon}
            >
              {t('pages.try_assistant.save_only')}
            </Button>
            {canResend && (
              <Button
                color="primary"
                size="sm"
                onPress={() => onSaveAndResend(index)}
                iconLeading={RefreshCcw01Icon}
              >
                {message.role === 'tool'
                  ? t('pages.try_assistant.save_and_regenerate')
                  : t('pages.try_assistant.save_and_resend')}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Try Assistant Page - Chat interface for testing assistants
 */
export function TryAssistantPage() {
  const { t } = useTranslation()
  const { id, conversationId: urlConversationId } = useParams<{ id: string; conversationId?: string }>()
  const navigate = useNavigate()
  const { user } = useIdentity()
  const assistantId = parseInt(id || '0', 10)

  // State
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [input, setInput] = useState('')
  const [callerNumber, setCallerNumber] = useState('')
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null)
  const [editingContent, setEditingContent] = useState('')
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [hasLoadedConversation, setHasLoadedConversation] = useState(false)

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const settingsButtonRef = useRef<HTMLButtonElement>(null)

  // API hooks
  // The assistant's number routes the chat like a call to that number
  const { phoneNumber: assistantPhoneNumber, isLoading: isLoadingAssistantPhoneNumber } =
    useAssistantPhoneNumber(assistantId)
  const tryAssistant = useTryAssistant(assistantPhoneNumber)
  const startConversation = useStartConversation(assistantPhoneNumber)
  const { isLoading: isIdentityLoading } = useIdentity()
  
  // Load existing conversation if conversationId is in URL
  const { data: existingConversation, isLoading: isLoadingConversation } = useConversation(
    urlConversationId ? parseInt(urlConversationId, 10) : undefined
  )

  const isLoading = tryAssistant.isPending || startConversation.isPending || isLoadingConversation

  // Scroll to bottom when messages change
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, scrollToBottom])

  // Load existing conversation messages when available
  useEffect(() => {
    // Skip if no conversation to load, or already loaded
    if (!existingConversation || hasLoadedConversation) return
    // Skip if still loading conversation
    if (isLoadingConversation) return
    // Wait for the number so the whole chat is routed the same way
    if (isLoadingAssistantPhoneNumber) return
    
    // Parse messages - handle both string and array formats
    let parsedMessages: ChatMessage[] = []
    if (existingConversation.messages) {
      if (Array.isArray(existingConversation.messages)) {
        parsedMessages = existingConversation.messages as ChatMessage[]
      } else if (typeof existingConversation.messages === 'string') {
        try {
          parsedMessages = JSON.parse(existingConversation.messages)
        } catch (error) {
          console.error('Failed to parse messages:', error)
        }
      }
    }
    
    // Set caller number from conversation's from_id
    const fromNumber = existingConversation.from_id
    if (fromNumber && !callerNumber) {
      setCallerNumber(fromNumber)
    }
    
    // Mark as loaded to prevent re-running
    setHasLoadedConversation(true)
    
    // Start conversation with loaded messages
    const effectiveCallerNumber = fromNumber || callerNumber || user?.phone_number
    startConversation.mutate(
      { 
        assistantId, 
        callerNumber: effectiveCallerNumber || undefined,
      },
      {
        onSuccess: (result) => {
          setConversationId(result.test_conversation_id)
          // Use the loaded messages instead of the empty response
          setMessages(parsedMessages)
        },
      }
    )
  }, [existingConversation, isLoadingConversation, isLoadingAssistantPhoneNumber, hasLoadedConversation, assistantId, callerNumber, user?.phone_number, startConversation])

  // Initialize fresh conversation when no existing conversation to load
  useEffect(() => {
    // Skip if we're loading or have loaded an existing conversation
    if (urlConversationId) return
    // Skip if no assistant ID, identity still loading, or already have a conversation
    if (!assistantId || isIdentityLoading || conversationId) return
    // Wait for the number so the whole chat is routed the same way
    if (isLoadingAssistantPhoneNumber) return
    
    // Skip if already loading
    if (startConversation.isPending) return

    // Use the user's phone number if available
    const userPhone = user?.phone_number
    if (userPhone && !callerNumber) {
      setCallerNumber(userPhone)
    }

    // Start the conversation
    startConversation.mutate(
      { assistantId, callerNumber: userPhone || callerNumber || undefined },
      {
        onSuccess: (result) => {
          setConversationId(result.test_conversation_id)
          setMessages(result.messages)
          // Only set caller number from API if user doesn't have one
          if (result.caller_number && !userPhone && !callerNumber) {
            setCallerNumber(result.caller_number)
          }
        },
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistantId, isIdentityLoading, isLoadingAssistantPhoneNumber, urlConversationId])

  // Restart conversation when caller number changes
  const handleCallerNumberChange = (newNumber: string) => {
    setCallerNumber(newNumber)
    // Reset conversation with new caller number
    if (newNumber !== callerNumber) {
      setMessages([])
      setConversationId(null)
      startConversation.mutate(
        { assistantId, callerNumber: newNumber },
        {
          onSuccess: (result) => {
            setConversationId(result.test_conversation_id)
            setMessages(result.messages)
          },
        }
      )
    }
  }

  // Send a message
  const sendMessage = async (
    userMessage: string,
    messageIndex = -1,
    providedMessages?: ChatMessage[]
  ) => {
    if (!userMessage.trim() || !conversationId) return

    const baseMessages = providedMessages || messages

    let updatedMessages: ChatMessage[]
    if (messageIndex === -1) {
      // New message
      updatedMessages = [...baseMessages, { role: 'user', content: userMessage }]
    } else {
      // Editing existing message - truncate after edit point
      updatedMessages = truncateAfterEdit(baseMessages, messageIndex)
      updatedMessages[messageIndex] = {
        ...updatedMessages[messageIndex],
        content: userMessage,
      }
    }

    // Ensure tool messages have correct tool_call_id
    updatedMessages = updatedMessages.map((msg, index) => {
      if (msg.role === 'tool' && !msg.tool_call_id) {
        for (let i = index - 1; i >= 0; i--) {
          if (updatedMessages[i].role === 'assistant' && updatedMessages[i].tool_calls) {
            return { ...msg, tool_call_id: updatedMessages[i].tool_calls![0].id }
          }
        }
      }
      return msg
    })

    setMessages(updatedMessages)
    setInput('')
    setEditingMessageId(null)

    const result = await tryAssistant.mutateAsync({
      assistant_id: assistantId,
      test_conversation_id: conversationId,
      caller_number: callerNumber || undefined,
      messages: updatedMessages.map((msg) => ({
        role: msg.role,
        content: msg.content,
        ...(msg.function_call && { function_call: msg.function_call }),
        ...(msg.tool_calls && { tool_calls: msg.tool_calls }),
        ...(msg.tool_call_id && { tool_call_id: msg.tool_call_id }),
        ...(msg.refusal && { refusal: msg.refusal }),
      })),
    })

    // Combine existing messages with new messages
    setMessages((prevMessages) => {
      const combinedMessages = [...prevMessages, ...result.messages]
      // Ensure tool_call_ids are preserved
      return combinedMessages.map((msg, index) => {
        if (msg.role === 'tool' && !msg.tool_call_id) {
          for (let i = index - 1; i >= 0; i--) {
            if (
              combinedMessages[i].role === 'assistant' &&
              combinedMessages[i].tool_calls
            ) {
              return { ...msg, tool_call_id: combinedMessages[i].tool_calls![0].id }
            }
          }
        }
        return msg
      })
    })
  }

  // Edit handlers
  const handleStartEdit = (index: number) => {
    setEditingMessageId(index)
    setEditingContent(messages[index].content || '')
  }

  const handleCancelEdit = () => {
    setEditingMessageId(null)
    setEditingContent('')
  }

  const handleSaveEdit = (index: number) => {
    const updatedMessages = [...messages]
    updatedMessages[index] = { ...updatedMessages[index], content: editingContent }
    setMessages(updatedMessages)
    setEditingMessageId(null)
    setEditingContent('')
  }

  const handleSaveAndResend = async (index: number) => {
    if (!editingContent.trim()) return

    const updatedMessages = [...messages]
    updatedMessages[index] = { ...updatedMessages[index], content: editingContent }

    setEditingMessageId(null)
    setEditingContent('')

    await sendMessage(editingContent, index, updatedMessages)
  }

  // Handle Enter key to send message
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage(input, -1)
    }
  }

  return (
    <div className="flex h-[calc(100vh-theme(spacing.8)-theme(spacing.16))] lg:h-[calc(100vh-theme(spacing.12))] flex-col bg-white dark:bg-gray-900 rounded-lg overflow-hidden">
      {/* Header */}
      <header className="shrink-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-900">
        <div className="flex items-center gap-3">
          <Button
            color="tertiary"
            size="sm"
            onPress={() => navigate('/assistants')}
            className="p-1"
            iconLeading={ArrowLeftIcon}
          />
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('pages.try_assistant.title')}
          </h1>
        </div>
        <div className="relative flex items-center gap-2">
          {callerNumber && (
            <div className="flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 dark:border-gray-700 dark:bg-gray-800">
              <Phone01Icon className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {callerNumber}
              </span>
            </div>
          )}
          <Button
            ref={settingsButtonRef}
            color="tertiary"
            size="sm"
            onPress={() => setIsSettingsOpen(!isSettingsOpen)}
            className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700"
            iconLeading={Settings01Icon}
          />
          <SettingsPopover
            isOpen={isSettingsOpen}
            onClose={() => setIsSettingsOpen(false)}
            callerNumber={callerNumber}
            onCallerNumberChange={handleCallerNumberChange}
            anchorRef={settingsButtonRef}
          />
        </div>
      </header>

      {/* Messages area */}
      <div className="relative flex-1 overflow-hidden">
        {/* Loading overlay for conversation start */}
        {(startConversation.isPending || isLoadingConversation) && messages.length === 0 && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm dark:bg-gray-900/80">
            <LoadingSpinner size="lg" />
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
              {isLoadingConversation ? t('pages.try_assistant.loading_conversation') : t('pages.try_assistant.starting_conversation')}
            </p>
          </div>
        )}

        {/* Scrollable message list */}
        <div className="h-full overflow-y-auto">
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {messages.map((message, index) => (
              <ChatMessageItem
                key={index}
                message={message}
                index={index}
                isEditing={editingMessageId === index}
                editContent={editingContent}
                onStartEdit={handleStartEdit}
                onCancelEdit={handleCancelEdit}
                onSaveEdit={handleSaveEdit}
                onSaveAndResend={handleSaveAndResend}
                onEditContentChange={setEditingContent}
              />
            ))}
          </div>
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input area */}
      <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-900">
        <div className="flex items-center gap-3">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('pages.try_assistant.type_message')}
            disabled={startConversation.isPending || !conversationId}
            autoComplete="off"
            className={cn(
              'flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-base',
              'placeholder:text-gray-400',
              'focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-none',
              'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
              'dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100'
            )}
          />
          <Button
            color="primary"
            size="md"
            onPress={() => sendMessage(input, -1)}
            isDisabled={!input.trim() || isLoading || !conversationId}
            iconLeading={Send01Icon}
          >
            {t('pages.try_assistant.send')}
          </Button>
          {tryAssistant.isPending && <LoadingSpinner size="sm" />}
        </div>
      </div>
    </div>
  )
}

export default TryAssistantPage
